import { z } from "zod";
import { SEVERITY_LEVELS } from "@shared/constants/severity.js";
import { CHILD_NAME_MAX, NOTES_MAX } from "@shared/constants/validation.js";

const ALLERGY_NAME_MAX = CHILD_NAME_MAX;
const EMERGENCY_ACTION_MAX = 500;
const DESCRIPTION_MAX = 1000;
// FARE emergency-care-plan fields. Symptoms and treatment get real room (they
// are read aloud in an emergency); the device location is a short pointer.
const REACTION_SYMPTOMS_MAX = 1000;
const TREATMENT_MAX = 1000;
const EPINEPHRINE_LOCATION_MAX = 200;

const nullableTrimmedString = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v && v.length > 0 ? v : null));

const updateNullableString = (max: number) =>
  z.string().trim().max(max).nullable().optional();

/**
 * Optional FK to the medication that treats this allergy. An unselected
 * <select> submits "", which must land as NULL rather than failing the uuid
 * check — so normalize before validating.
 */
const rescueMedicationIdField = z
  .string()
  .trim()
  .nullish()
  .transform((v) => (v && v.length > 0 ? v : null))
  .refine((v) => v === null || z.string().uuid().safeParse(v).success, {
    message: "Invalid medication",
  });

export const createAllergySchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Allergy name is required")
    .max(ALLERGY_NAME_MAX, "Allergy name is too long"),
  severity: z.enum(SEVERITY_LEVELS, {
    errorMap: () => ({ message: "Select a severity" }),
  }),
  description: nullableTrimmedString(DESCRIPTION_MAX),
  emergencyAction: nullableTrimmedString(EMERGENCY_ACTION_MAX),
  reactionSymptoms: nullableTrimmedString(REACTION_SYMPTOMS_MAX),
  treatment: nullableTrimmedString(TREATMENT_MAX),
  epinephrineLocation: nullableTrimmedString(EPINEPHRINE_LOCATION_MAX),
  rescueMedicationId: rescueMedicationIdField,
  notes: nullableTrimmedString(NOTES_MAX),
});

export const updateAllergySchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Allergy name is required")
    .max(ALLERGY_NAME_MAX)
    .optional(),
  severity: z.enum(SEVERITY_LEVELS).optional(),
  description: updateNullableString(DESCRIPTION_MAX),
  emergencyAction: updateNullableString(EMERGENCY_ACTION_MAX),
  reactionSymptoms: updateNullableString(REACTION_SYMPTOMS_MAX),
  treatment: updateNullableString(TREATMENT_MAX),
  epinephrineLocation: updateNullableString(EPINEPHRINE_LOCATION_MAX),
  rescueMedicationId: rescueMedicationIdField,
  notes: updateNullableString(NOTES_MAX),
});

export type CreateAllergyInput = z.infer<typeof createAllergySchema>;
export type UpdateAllergyInput = z.infer<typeof updateAllergySchema>;
